import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  getNotificaciones, marcarTodasLeidas, marcarLeida, borrarNotificacion, borrarTodasNotificaciones,
} from "../../features/portal/services/portalService";

// Sondeo sin websockets. La administración cada 4s (se siente casi inmediato);
// el residente cada 30s: puede haber cientos conectados y para sus avisos no
// hace falta más. Con la pestaña oculta no se sondea (ver más abajo).
const POLL_ADMIN = 4000;
const POLL_RESIDENTE = 30000;

const NotificationContext = createContext(null);

function condominioActivo() {
  return localStorage.getItem("active_condominio_id") || "";
}

export function NotificationProvider({ children }) {
  const [notifs,      setNotifs]      = useState([]);
  const [unread,      setUnread]      = useState(0);
  const [justArrived, setJustArrived] = useState([]);
  const seenIds = useRef(null); // null = todavía no hicimos el primer sondeo
  // De qué condominio es lo que tenemos en pantalla. El selector recarga la
  // página al cambiar, pero se puede cambiar desde otra pestaña: ahí este
  // provider sigue vivo y no debe seguir mostrando lo del condominio anterior.
  const condoRef = useRef(condominioActivo());

  const poll = useCallback(async () => {
    const condoAlPedir = condominioActivo();
    try {
      const data = await getNotificaciones();
      // Si cambiaron de condominio mientras la petición iba y venía, esta
      // respuesta ya es de otro condominio: se descarta.
      if (condominioActivo() !== condoAlPedir) return;
      // El backend ya filtra por el condominio activo; esto es el cinturón de
      // seguridad para que nunca se liste un aviso de otro condominio.
      const list = (data.results || []).filter(
        n => !n.condominio || !condoAlPedir || String(n.condominio) === condoAlPedir
      );
      setNotifs(list);
      setUnread(data.unread || 0);

      if (seenIds.current !== null) {
        const nuevas = list.filter(n => !seenIds.current.has(n.id));
        if (nuevas.length > 0) {
          setJustArrived(prev => [...prev, ...nuevas]);
          // Si hay alguien viendo la página relacionada, que se refresque sola
          // en vez de esperar a que la persona la recargue a mano.
          nuevas.forEach(n => {
            window.dispatchEvent(new CustomEvent("portal:data-changed", { detail: { tipo: n.tipo, link: n.link } }));
          });
        }
      }
      seenIds.current = new Set(list.map(n => n.id));
    } catch { /* silencioso — no queremos que un poll fallido rompa la pantalla */ }
  }, []);

  useEffect(() => {
    // El operador de agua no tiene acceso al portal: sondearlo solo generaría
    // un 403 cada pocos segundos, sin ninguna notificación que mostrar.
    const rol = localStorage.getItem("rol");
    if (["AGUA", "SEGURIDAD"].includes(rol)) return undefined;
    const intervalo = rol === "RESIDENTE" ? POLL_RESIDENTE : POLL_ADMIN;
    // Pestaña en segundo plano: no se gasta tráfico. Al volver, se pone al día.
    const tick = () => { if (!document.hidden) poll(); };
    const alVolver = () => { if (!document.hidden) poll(); };
    // Cambio de condominio: la lista en pantalla es del anterior, así que se
    // vacía de una y se vuelve a sondear. Sin esto quedaban hasta cuatro
    // segundos a la vista los avisos del condominio que se acaba de dejar.
    const alCambiarCondominio = () => {
      const ahora = condominioActivo();
      if (ahora === condoRef.current) return;
      condoRef.current = ahora;
      setNotifs([]);
      setUnread(0);
      setJustArrived([]);
      // Las del condominio nuevo no son "recién llegadas": no deben saltar
      // como toasts solo por haber cambiado de condominio.
      seenIds.current = null;
      poll();
    };
    poll();
    const timer = setInterval(tick, intervalo);
    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("storage", alCambiarCondominio);
    window.addEventListener("condominio-changed", alCambiarCondominio);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", alVolver);
      window.removeEventListener("storage", alCambiarCondominio);
      window.removeEventListener("condominio-changed", alCambiarCondominio);
    };
  }, [poll]);

  const dismissArrived = useCallback((id) => {
    setJustArrived(prev => prev.filter(n => n.id !== id));
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await marcarTodasLeidas();
      setUnread(0);
      setNotifs(prev => prev.map(n => ({ ...n, leida: true })));
    } catch { /* silencioso */ }
  }, []);

  /**
   * Marca una como leída. Devuelve el aviso cuando resultó ser de otro
   * condominio, para que quien la abrió muestre el mensaje en vez de navegar.
   */
  const markOneRead = useCallback(async (id) => {
    try {
      const r = await marcarLeida(id);
      if (r?.otroCondominio) {
        // No es de este condominio: sacarla de la lista en vez de dejarla ahí.
        setNotifs(prev => prev.filter(n => n.id !== id));
        return r;
      }
      setNotifs(prev => prev.map(n => (n.id === id ? { ...n, leida: true } : n)));
    } catch { /* silencioso */ }
    return { otroCondominio: false };
  }, []);

  const deleteOne = useCallback(async (id) => {
    const antes = notifs;
    setNotifs(prev => prev.filter(n => n.id !== id));
    try {
      await borrarNotificacion(id);
    } catch {
      setNotifs(antes);   // si falló, vuelve a aparecer
    }
  }, [notifs]);

  const deleteAll = useCallback(async () => {
    try {
      await borrarTodasNotificaciones();
      setNotifs([]);
      setUnread(0);
    } catch { /* silencioso */ }
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifs, unread, justArrived, dismissArrived, markAllRead, markOneRead, deleteOne, deleteAll, refetch: poll,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications debe usarse dentro de <NotificationProvider>");
  return ctx;
}
